import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { StripeProvider } from '@/common/contexts/stripe/StripeProvider';
import { useGetMealConfiguration } from '@/common/integrations/menhir/employee/meal/meal.hooks';
import { TMealConfiguration } from '@/common/integrations/menhir/employee/meal/meal.types';
import { useGetPaymentMethods } from '@/common/integrations/menhir/employee/payments/payments.hooks';
import { FlowLayout } from '@/common/layouts/FlowLayout/FlowLayout';
import { AutomaticPaymentConfirmStep } from './AutomaticPaymentConfirmStep/AutomaticPaymentConfirmStep';
import { AutomaticPaymentMethodStep } from './AutomaticPaymentMethodStep/AutomaticPaymentMethodStep';

export function AutomaticPaymentSetupPage() {
  const { mealConfiguration, isInitialLoading: configLoading } =
    useGetMealConfiguration();
  const { paymentMethods, isInitialLoading: paymentMethodsLoading } =
    useGetPaymentMethods();

  if (configLoading || paymentMethodsLoading) {
    return <LoadingPlaceholder />;
  }

  return (
    <AutomaticPaymentSetupPageContent
      singlePaymentConfig={mealConfiguration?.singleTransaction}
      paymentMethodsCount={paymentMethods?.length ?? 0}
    />
  );
}

export type TAutomaticPaymentSetupPageContentProps = {
  singlePaymentConfig?: TMealConfiguration['singleTransaction'];
  paymentMethodsCount: number;
};

export function AutomaticPaymentSetupPageContent({
  singlePaymentConfig,
  paymentMethodsCount,
}: TAutomaticPaymentSetupPageContentProps) {
  const [hasPaymentMethods] = useState(paymentMethodsCount > 0);
  const { t } = useTranslation();

  const isEditing = !!singlePaymentConfig;

  return (
    <StripeProvider>
      <FlowLayout.Root
        headerProps={{
          title: isEditing
            ? t('dashboard.automatic_payment.setup.title_change')
            : t('dashboard.automatic_payment.setup.title'),
        }}
      >
        {!hasPaymentMethods && (
          <FlowLayout.Step
            title={t(
              'dashboard.automatic_payment.setup.step.payment_method.title'
            )}
            description={t(
              'dashboard.automatic_payment.setup.step.payment_method.description'
            )}
          >
            <AutomaticPaymentMethodStep />
          </FlowLayout.Step>
        )}
        <FlowLayout.Step
          title={t('dashboard.automatic_payment.setup.step.confirm.title')}
        >
          <AutomaticPaymentConfirmStep currentConfig={singlePaymentConfig} />
        </FlowLayout.Step>
      </FlowLayout.Root>
    </StripeProvider>
  );
}
